import {
  COHORT_METHOD_OPTIONS,
  FILE_ANALYSES,
  GENESUS_AUTOMATIC_COHORT,
  GROUP_ANALYSES,
  WORKFLOW_DEFAULTS,
  aiAvailable,
  cnvAvailable,
  suggestBatchName,
  validateWorkflow as validateStartWorkflow,
} from 'src/services/analysisStart'
import { isEnabled } from './features'
import { readJson, removeKey, writeJson } from './storage'

// ----------------------------------------------------------------------
// Workflow rules for the New analysis panel. They mirror the Home wizard
// (AnnotationView and its StepConfigure / StepClinicalData): which options apply,
// their defaults and their validation. Defaults, validation and the start payload
// come from src/services/analysisStart.js, so the panel and the start agree.
//
// workflow = {
//   analysis: 'germline' | 'somatic'        file analyses: one start per sample
//           | 'multisample' | 'cohort' | 'carrier',   group analyses (groups/**)
//   input: 'fastq' | 'ont' | 'pacbio' | 'vcf',
//   mode: 'to' | 'tn' | 'rna',               somatic FASTQ only ('rna' = tumour RNA)
//   seq: 'wes' | 'ces' | 'wgs' | null,       FASTQ only; no default, as the wizard
//   kitId,                                   BedFile.name, sent as `panel` (the wizard's captureKit)
//   kitName,                                 BedFile.visible_name, for display only
//   cnv, cnvMethod, cnvCohortId,             germline FASTQ exome only
//   lrCnv, lrSv, lrMeth,                     ONT / PacBio
//   reference: 'GRCh38' | 'GRCh37' | null,   VCF only; no default, as the wizard
//   tumourType: { oncotree_code, name } | null,
//   interpretation: 'standard' | 'ai',       germline only
//   cohortMethod, cohortName,                CNV cohort ('genesus-cnv' | 'exomedepth');
//                                            the cohort's kit is kitId / kitName
//   carrierConfigId, carrierConfigTitle,
//   carrierSamplesRequired,                  Carrier panel
// }
//
// A group analysis leaves the file fields (input, seq, kit, CNV, long-read ...) as
// they were, so going back to Germline or Somatic finds them again.
// ----------------------------------------------------------------------

export { COHORT_METHOD_OPTIONS, FILE_ANALYSES, GENESUS_AUTOMATIC_COHORT, GROUP_ANALYSES }

export const BASE_WORKFLOW = { ...WORKFLOW_DEFAULTS }

// The analysis tiles, in order. Carrier shows only with FEATURES.carrier.
const ALL_ANALYSIS_OPTIONS = [
  { value: 'germline', title: 'Germline', hint: 'Inherited variants' },
  { value: 'somatic', title: 'Somatic', hint: 'Tumour samples' },
  { value: 'multisample', title: 'Multisample', hint: 'Shared variants' },
  { value: 'cohort', title: 'CNV cohort', hint: 'CNV reference set' },
  { value: 'carrier', title: 'Carrier', hint: 'Couple screening' },
]
export const ANALYSIS_OPTIONS = ALL_ANALYSIS_OPTIONS.filter((o) => o.value !== 'carrier' || isEnabled('carrier'))

// Takes a workflow or an analysis name.
const analysisOf = (wf) => (typeof wf === 'string' ? wf : wf?.analysis)
export const isFileAnalysis = (wf) => FILE_ANALYSES.includes(analysisOf(wf))
export const isGroupAnalysis = (wf) => GROUP_ANALYSES.includes(analysisOf(wf))

// One line under the Input control.
export const INPUT_HINT = {
  fastq: 'Paired-end short reads',
  ont: 'Nanopore long reads',
  pacbio: 'PacBio HiFi long reads',
  vcf: 'SNV/Indel, CNV or SV calls',
}

// The tools behind each long-read option (the wizard's labels).
export const LONG_READ_TOOLS = {
  ont: { cnv: 'Spectre', sv: 'Sniffles2', meth: 'modkit' },
  pacbio: { cnv: 'HiFiCNV', sv: 'pbsv', meth: 'pb-CpG-tools' },
}

export const INPUT_OPTIONS = {
  germline: [
    { value: 'fastq', label: 'FASTQ' },
    { value: 'ont', label: 'ONT BAM' },
    { value: 'pacbio', label: 'PacBio BAM' },
    { value: 'vcf', label: 'VCF' },
  ],
  somatic: [
    { value: 'fastq', label: 'FASTQ' },
    { value: 'vcf', label: 'VCF' },
  ],
}

export const INPUT_LABEL = { fastq: 'FASTQ', ont: 'ONT BAM', pacbio: 'PacBio BAM', vcf: 'VCF' }
export const INPUT_LONG_LABEL = { fastq: 'Paired-end FASTQ', ont: 'ONT BAM', pacbio: 'PacBio HiFi BAM', vcf: 'VCF' }

export const SEQ_OPTIONS = [
  { value: 'wes', label: 'Whole exome', short: 'WES' },
  { value: 'ces', label: 'Clinical exome', short: 'CES' },
  { value: 'wgs', label: 'Whole genome', short: 'WGS' },
]
export const SEQ_LABEL = { wes: 'Whole exome', ces: 'Clinical exome', wgs: 'Whole genome' }

export const MODE_OPTIONS = [
  { value: 'to', label: 'Tumour-only' },
  { value: 'tn', label: 'Tumour-normal' },
  { value: 'rna', label: 'Tumour RNA' },
]

export const CNV_METHODS = [
  { value: 'genesus-cnv', label: 'Genesus CNV', meta: 'Recommended' },
  { value: 'ecole', label: 'ECOLE' },
  { value: 'exomedepth', label: 'ExomeDepth' },
]
export const CNV_METHOD_LABEL = Object.fromEntries(CNV_METHODS.map((m) => [m.value, m.label]))

export const REFERENCE_OPTIONS = [
  { value: 'GRCh38', label: 'GRCh38' },
  { value: 'GRCh37', label: 'GRCh37' },
]

// Which groups apply. The file options never apply to a group analysis, whose
// workflow keeps them only for the way back.
export const isFastq = (wf) => !isGroupAnalysis(wf) && wf.input === 'fastq'
// Tumour RNA takes a library per sample instead of a sequencing type and kit.
export const isRnaWorkflow = (wf) => isFastq(wf) && wf.analysis === 'somatic' && wf.mode === 'rna'
export const seqApplies = (wf) => isFastq(wf) && !isRnaWorkflow(wf)
export const needsKit = (wf) => seqApplies(wf) && (wf.seq === 'wes' || wf.seq === 'ces')
export const kitApplies = (wf) => needsKit(wf) || analysisOf(wf) === 'cohort'
export const cnvApplies = (wf) => cnvAvailable(wf)
export const longReadApplies = (wf) => wf.analysis === 'germline' && (wf.input === 'ont' || wf.input === 'pacbio')
export const referenceApplies = (wf) => !isGroupAnalysis(wf) && wf.input === 'vcf'
export const modeApplies = (wf) => wf.analysis === 'somatic' && wf.input === 'fastq'
export const tumourTypeApplies = (wf) => wf.analysis === 'somatic'
export const isTumourNormal = (wf) => modeApplies(wf) && wf.mode === 'tn'

// AI never runs on somatic analyses nor on CNV/SV VCFs (AnnotationView aiUnavailable).
// The control shows while at least one sample (or no sample yet) can use it.
export const aiApplies = (wf, samples = []) =>
  wf.analysis === 'germline' && (samples.length === 0 || samples.some((s) => aiAvailable(wf, s)))

const SOMATIC_INPUTS = ['fastq', 'vcf']
const inputFits = (analysis, input) =>
  analysis === 'somatic' ? SOMATIC_INPUTS.includes(input) : Boolean(INPUT_LABEL[input])

/**
 * Keeps a workflow consistent after a change (mirrors the wizard's resets). A group
 * analysis is left as it is: its file fields wait for the way back, and a cohort
 * keeps its kit.
 */
export const normaliseWorkflow = (wf) => {
  const next = { ...wf }
  if (isGroupAnalysis(next)) return next
  if (next.analysis === 'somatic' && !SOMATIC_INPUTS.includes(next.input)) next.input = 'fastq'
  if (next.analysis === 'somatic') next.interpretation = 'standard'
  if (!cnvApplies(next)) {
    next.cnv = false
    next.cnvMethod = 'genesus-cnv'
    next.cnvCohortId = GENESUS_AUTOMATIC_COHORT
  }
  if (!longReadApplies(next)) {
    next.lrCnv = false
    next.lrSv = false
    next.lrMeth = false
  }
  if (!needsKit(next)) {
    next.kitId = ''
    next.kitName = ''
  }
  if (!referenceApplies(next)) next.reference = null
  if (!tumourTypeApplies(next)) next.tumourType = null
  return next
}

// ----------------------------------------------------------------------
// Switching analysis or input. Each file slot (`${analysis}:${input}`) keeps its own
// options, so a look at another analysis or input loses nothing. The capture kit is
// shared between the FASTQ exome slots and CNV cohort: the kit picked last wins
// when one of them is entered from a group analysis.
//
// memory = {
//   'germline:fastq': { mode, seq, kitId, ... },   one entry per file slot left
//   lastInput: { germline: 'pacbio', somatic: 'fastq' },
//   kit: { kitId, kitName },                        the kit picked last
// }

export const SLOT_FIELDS = [
  'mode',
  'seq',
  'kitId',
  'kitName',
  'cnv',
  'cnvMethod',
  'cnvCohortId',
  'lrCnv',
  'lrSv',
  'lrMeth',
  'reference',
  'interpretation',
  'tumourType',
]

const slotKeyOf = (wf) => (isFileAnalysis(wf) ? `${wf.analysis}:${wf.input}` : null)
const has = (patch, field) => Object.prototype.hasOwnProperty.call(patch, field)
const pick = (wf, fields) => Object.fromEntries(fields.map((field) => [field, wf[field]]))

/**
 * Applies a workflow change and keeps each slot's options.
 *
 * Leaving a file slot saves its options in `memory`; entering one restores what it
 * had. Fields in `patch` always win. When the patch changes the analysis but not the
 * input, the analysis gets back its last input (Germline PacBio, then Somatic, then
 * Germline is PacBio again, with its CNV, SV and Methylation). Group analyses leave
 * the file fields alone. Pure: `prev` and `memory` are not changed.
 *
 * @returns {{ workflow, memory }}
 */
export const switchWorkflow = (prev, patch = {}, memory = {}) => {
  const current = { ...BASE_WORKFLOW, ...(prev || {}) }
  const change = patch || {}
  const mem = memory && typeof memory === 'object' ? memory : {}
  const target = { ...current, ...change }

  if (isFileAnalysis(target) && !has(change, 'input') && target.analysis !== current.analysis) {
    const last = mem.lastInput?.[target.analysis]
    if (inputFits(target.analysis, last)) target.input = last
  }
  if (isFileAnalysis(target) && !inputFits(target.analysis, target.input)) target.input = 'fastq'

  const from = slotKeyOf(current)
  const to = slotKeyOf(target)
  if (target.analysis === current.analysis && from === to) return { workflow: normaliseWorkflow(target), memory: mem }

  const next = { ...mem }
  if (from) {
    next[from] = pick(current, SLOT_FIELDS)
    next.lastInput = { ...(mem.lastInput || {}), [current.analysis]: current.input }
  }
  if (kitApplies(current) && current.kitId) next.kit = { kitId: current.kitId, kitName: current.kitName || '' }

  let workflow = target
  if (to && next[to]) {
    // Restore the slot, then let the patch win.
    const restored = { ...next[to] }
    Object.keys(change).forEach((field) => delete restored[field])
    workflow = { ...workflow, ...restored }
  }
  const sharedKit = !has(change, 'kitId') && next.kit?.kitId ? next.kit : null
  if (sharedKit && (target.analysis === 'cohort' || (to && isGroupAnalysis(current)))) {
    workflow = { ...workflow, kitId: sharedKit.kitId, kitName: sharedKit.kitName }
  }
  return { workflow: normaliseWorkflow(workflow), memory: next }
}

// A cohort (GATK or ExomeDepth) built on the selected kit.
export const cohortMatchesKit = (cohort, kitId) => {
  const bed = cohort?.bed_file
  if (bed) return bed.name === kitId || bed.original_name === kitId
  return cohort?.bed_file_name === kitId || cohort?.bed_file_original_name === kitId
}

const ERROR_FIELD = { kitId: 'kit', cnvCohortId: 'cohort', carrierConfigId: 'carrierConfig' }

/**
 * Validation shown in the workflow column (analysisStart.validateWorkflow, keyed by
 * the column's groups); the review stays blocked while any is set. Group analyses
 * use the keys kit, cohortName, cohortMethod (CNV cohort) and carrierConfig.
 * @returns {{ [field]: string }}
 */
export const validateWorkflow = (wf) => {
  const errors = {}
  Object.entries(validateStartWorkflow(wf)).forEach(([key, message]) => {
    errors[ERROR_FIELD[key] || key] = /[.!?]$/.test(message) ? message : `${message}.`
  })
  return errors
}

export const hasReference = (wf) => wf.reference === 'GRCh38' || wf.reference === 'GRCh37'

// ----------------------------------------------------------------------
// Price: see ./pricing (priceOf, batchPrice, groupPrice). formatCredits stays
// importable from here.

export { formatCredits } from './pricing'

// ----------------------------------------------------------------------
// Names and sentences

/**
 * "WES germline 29 Sep", used as the batch name placeholder for 2+ samples;
 * "Multisample 29 Sep", "Cohort 29 Sep" or "Carrier 29 Sep" for group analyses.
 */
export const autoBatchName = (wf, date = new Date()) => suggestBatchName(wf, date)

/** "Whole exome on Twist Exome v2, CNV with Genesus CNV (automatic cohort), standard interpretation." */
export const workflowSentence = (wf, { cohortName } = {}) => {
  const parts = []
  if (wf.input === 'fastq') {
    let s = wf.analysis === 'somatic' ? `${isTumourNormal(wf) ? 'Tumour-normal' : 'Tumour-only'}, ` : ''
    s += SEQ_LABEL[wf.seq] || 'FASTQ'
    if (needsKit(wf) && wf.kitName) s += ` on ${wf.kitName}`
    parts.push(s)
    if (cnvApplies(wf) && wf.cnv) {
      const method = CNV_METHOD_LABEL[wf.cnvMethod] || 'CNV'
      const cohort =
        wf.cnvMethod === 'genesus-cnv' || wf.cnvMethod === 'exomedepth'
          ? !wf.cnvCohortId || wf.cnvCohortId === GENESUS_AUTOMATIC_COHORT
            ? ' (automatic cohort)'
            : cohortName
            ? ` (cohort ${cohortName})`
            : ''
          : ''
      parts.push(`CNV with ${method}${cohort}`)
    }
  } else if (wf.input === 'vcf') {
    parts.push(`VCF on ${hasReference(wf) ? wf.reference : 'a reference to choose'}`)
  } else {
    const extras = [wf.lrCnv && 'CNV', wf.lrSv && 'SV', wf.lrMeth && 'methylation'].filter(Boolean)
    parts.push(`${INPUT_LONG_LABEL[wf.input]}${extras.length ? ` with ${extras.join(', ')}` : ''}`)
  }
  if (wf.analysis === 'somatic') {
    parts.push(wf.tumourType ? `default tumour type ${wf.tumourType.name}` : 'no default tumour type')
  } else {
    // 'mixed': some samples run AI and some do not; the review says how many.
    if (wf.interpretation !== 'mixed') {
      parts.push(wf.interpretation === 'ai' ? 'AI-assisted interpretation' : 'standard interpretation')
    }
  }
  const sentence = parts.join(', ')
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`
}

// ----------------------------------------------------------------------
// The last workflow a start used, remembered per user in this browser so a routine
// case needs no clicks. Only workflow choices are kept: the default tumour type
// belongs to the samples of that run, and the reference genome belongs to the VCFs
// of that run (a VCF on the other build would be read wrongly), so both are left out
// and every VCF run asks for its reference. Values read back are checked here (known
// options only); the kit and the CNV cohort are checked against the server's lists
// by reconcileKit and reconcileCohort once loaded.

// The key before workflows were kept per user; removed on the next save.
const LEGACY_LAST_WORKFLOW_KEY = 'genesus.newAnalysis.lastWorkflow'

export const lastWorkflowKey = (userKey) => `${LEGACY_LAST_WORKFLOW_KEY}.${userKey || 'anonymous'}`

export const REMEMBERED_FIELDS = [
  'analysis',
  'input',
  'mode',
  'seq',
  'kitId',
  'kitName',
  'cnv',
  'cnvMethod',
  'cnvCohortId',
  'lrCnv',
  'lrSv',
  'lrMeth',
  'interpretation',
]

const isCohortId = (id) => (typeof id === 'string' && id !== '') || (typeof id === 'number' && Number.isFinite(id))
const usesCohort = (method) => method === 'genesus-cnv' || method === 'exomedepth'
const defaultCohortFor = (method) => (method === 'exomedepth' ? '' : GENESUS_AUTOMATIC_COHORT)

/**
 * A complete, consistent workflow from stored values, or null when nothing usable
 * is stored. Unknown options are dropped and fall back to the defaults.
 */
export const sanitiseWorkflow = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  // Group analyses are chosen each time; they are never remembered.
  if (isGroupAnalysis(raw)) return null
  const wf = {}
  if (raw.analysis === 'germline' || raw.analysis === 'somatic') wf.analysis = raw.analysis
  if (INPUT_LABEL[raw.input]) wf.input = raw.input
  if (raw.mode === 'to' || raw.mode === 'tn' || raw.mode === 'rna') wf.mode = raw.mode
  if (SEQ_LABEL[raw.seq]) wf.seq = raw.seq
  if (typeof raw.kitId === 'string' && raw.kitId) {
    wf.kitId = raw.kitId
    wf.kitName = typeof raw.kitName === 'string' ? raw.kitName : ''
  }
  ;['cnv', 'lrCnv', 'lrSv', 'lrMeth'].forEach((field) => {
    if (typeof raw[field] === 'boolean') wf[field] = raw[field]
  })
  if (CNV_METHOD_LABEL[raw.cnvMethod]) wf.cnvMethod = raw.cnvMethod
  if (isCohortId(raw.cnvCohortId)) wf.cnvCohortId = raw.cnvCohortId
  if (raw.reference === 'GRCh38' || raw.reference === 'GRCh37') wf.reference = raw.reference
  if (raw.interpretation === 'standard' || raw.interpretation === 'ai') wf.interpretation = raw.interpretation
  if (!Object.keys(wf).length) return null

  const next = { ...BASE_WORKFLOW, ...wf }
  // A cohort only means something with the method it was chosen for.
  if (!usesCohort(next.cnvMethod) || !isCohortId(next.cnvCohortId)) next.cnvCohortId = defaultCohortFor(next.cnvMethod)
  else if (next.cnvMethod === 'exomedepth' && next.cnvCohortId === GENESUS_AUTOMATIC_COHORT) next.cnvCohortId = ''
  return normaliseWorkflow(next)
}

const pickRemembered = (wf) =>
  wf && typeof wf === 'object' && !Array.isArray(wf)
    ? Object.fromEntries(REMEMBERED_FIELDS.map((field) => [field, wf[field]]))
    : null

/**
 * The user's last workflow, checked, or null. Fields that are no longer remembered
 * (a reference genome saved before every VCF asked for it) are ignored.
 */
export const loadLastWorkflow = (userKey) => {
  const stored = readJson(lastWorkflowKey(userKey))
  return stored && typeof stored === 'object' ? sanitiseWorkflow(pickRemembered(stored.workflow)) : null
}

/** For Home's "Last used" line: the user's last workflow, or null. */
export const getLastWorkflow = (userKey) => loadLastWorkflow(userKey)

/**
 * Remembers a started workflow for this user, without sample-specific fields.
 * False for a group analysis, which is never remembered.
 */
export const saveLastWorkflow = (wf, userKey) => {
  if (isGroupAnalysis(wf)) return false
  const workflow = sanitiseWorkflow(wf)
  if (!workflow) return false
  removeKey(LEGACY_LAST_WORKFLOW_KEY)
  return writeJson(lastWorkflowKey(userKey), { workflow: pickRemembered(workflow), savedAt: Date.now() })
}

/** A kit's display name: its visible name without a trailing ".bed". */
export const kitLabelOf = (bed) => {
  const full = (bed && (bed.visible_name || bed.original_name || bed.name)) || ''
  return full.replace(/\.bed$/i, '') || full
}

/**
 * Drops a capture kit (of a FASTQ exome run or a CNV cohort) that no longer exists
 * or cannot be chosen (not verified, or a library chemistry Genesus cannot analyse),
 * and refreshes the kit's display name. Returns
 * the same object when nothing changes.
 */
export const reconcileKit = (wf, bedFiles) => {
  if (!kitApplies(wf) || !wf.kitId || !Array.isArray(bedFiles)) return wf
  const bed = bedFiles.find((b) => b && b.name === wf.kitId)
  if (!bed || bed.is_processed === false || !bed.is_verified || bed.own_calling?.available === false) {
    if (isGroupAnalysis(wf)) return { ...wf, kitId: '', kitName: '' }
    return { ...wf, kitId: '', kitName: '', cnvCohortId: defaultCohortFor(wf.cnvMethod) }
  }
  const kitName = kitLabelOf(bed)
  return kitName === wf.kitName ? wf : { ...wf, kitName }
}

/**
 * Drops a CNV cohort that is gone, unfinished or built on another kit, given the
 * cohort list for `method` (the response WorkflowColumn reads). Returns the same
 * object when nothing changes.
 */
export const reconcileCohort = (wf, method, data) => {
  if (!wf.cnv || !cnvApplies(wf) || wf.cnvMethod !== method || !usesCohort(method)) return wf
  const id = wf.cnvCohortId
  if (!isCohortId(id) || id === GENESUS_AUTOMATIC_COHORT) return wf
  const cohorts = Array.isArray(data?.cohorts) ? data.cohorts : []
  const usable = cohorts.some(
    (c) => c && String(c.id) === String(id) && c.status === 'COMPLETED' && cohortMatchesKit(c, wf.kitId),
  )
  return usable ? wf : { ...wf, cnvCohortId: defaultCohortFor(method) }
}

/** True when the workflow names a specific CNV cohort, which the panel checks once loaded. */
export const namesCohort = (wf) =>
  Boolean(wf.cnv && cnvApplies(wf) && usesCohort(wf.cnvMethod) && isCohortId(wf.cnvCohortId)) &&
  wf.cnvCohortId !== GENESUS_AUTOMATIC_COHORT
